// A pasted screenshot is often several MB at full resolution. The note stores
// it inline and the member's triage list returns every note in full, so the
// editor shrinks it before it is inserted (BL-TASK-1423).
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

export const fitWithin = (
  width: number,
  height: number,
  maxEdge = MAX_EDGE,
): { width: number; height: number } => {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

// JPEG unless the image needs its transparency and PNG keeps it for less.
export const pickEncoding = ({
  hasAlpha,
  jpeg,
  png,
}: {
  hasAlpha: boolean;
  jpeg: Blob;
  png?: Blob;
}): Blob => (hasAlpha && png && png.size < jpeg.size ? png : jpeg);

const loadImage = (file: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The image could not be read'));
    };
    image.src = url;
  });

const encode = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob> =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Encode failed'))),
      type,
      quality,
    ),
  );

const hasTransparency = (pixels: Uint8ClampedArray): boolean => {
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] < 255) return true;
  }
  return false;
};

/** GIFs are not passed here: re-encoding would drop their animation. */
export const downscaleImage = async (file: Blob): Promise<Blob> => {
  const image = await loadImage(file);
  const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas unavailable');
  context.drawImage(image, 0, 0, width, height);

  const hasAlpha =
    file.type !== 'image/jpeg' &&
    hasTransparency(context.getImageData(0, 0, width, height).data);
  const png = hasAlpha ? await encode(canvas, 'image/png') : undefined;
  // JPEG has no alpha channel; without a backdrop transparent areas go black.
  context.globalCompositeOperation = 'destination-over';
  context.fillStyle = '#FFFFFF';
  context.fillRect(0, 0, width, height);
  const jpeg = await encode(canvas, 'image/jpeg', JPEG_QUALITY);

  return pickEncoding({ hasAlpha, jpeg, png });
};
